import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getVenueOffers, isCustomer, PublicOffer } from '../../../api';
import { trackEvent } from '../../../analytics';
import { createHangoutInvite, getHangoutInvite, getHangoutVenue, toApiError, updateHangoutInvite } from '../api';
import { ApiError, HangoutVenue, InviteType } from '../types';
import { CreateInviteErrors, loginUrlFor, MAX_INVITE_MESSAGE, toDateInput, toOffsetIso, toTimeInput, validateCreateInvite } from '../utils';
import { ErrorState, PageTop, Skeleton } from '../components/common';
import { InviteTypeSelector, VenueInviteCard } from '../components/InviteParts';
import '../hangouts.css';

const DEFAULT_MESSAGE_HINT = "Night out at the club? 🎉\nLet's meet and have a great night!\nJoin me on Cheers2Night.";

// The draft this browser tab already created for a venue, so Back → Continue updates it instead
// of creating a second invite.
const draftKey = (venueId: number) => `c2n_hangout_draft_${venueId}`;
const readDraft = (venueId: number): number | null => {
  try { return Number(sessionStorage.getItem(draftKey(venueId))) || null; } catch { return null; }
};
export const forgetDraft = (venueId: number) => { try { sessionStorage.removeItem(draftKey(venueId)); } catch { /* ignore */ } };

// Default: tonight at 9:30 PM, or tomorrow if that has passed.
function defaultWhen(): Date {
  const d = new Date();
  d.setHours(21, 30, 0, 0);
  if (d.getTime() < Date.now() + 30 * 60 * 1000) d.setDate(d.getDate() + 1);
  return d;
}

// Screen 2 "Create Invite" (/venues/:venueId/invite), also used as "Edit Invite" (/invites/:inviteId/edit).
const CreateInvitePage: React.FC = () => {
  const params = useParams<{ venueId?: string; inviteId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const editId = params.inviteId ? Number(params.inviteId) : null;

  const [venueId, setVenueId] = useState<number | null>(params.venueId ? Number(params.venueId) : null);
  const [venue, setVenue] = useState<HangoutVenue | null>(null);
  const [offers, setOffers] = useState<PublicOffer[]>([]);
  const [offerId, setOfferId] = useState<number | null>(null);
  const when = useMemo(defaultWhen, []);
  const [date, setDate] = useState(toDateInput(when));
  const [time, setTime] = useState(toTimeInput(when));
  const [inviteType, setInviteType] = useState<InviteType | ''>('FriendsOfFriends');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<CreateInviteErrors>({});
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Editing: load the invite and prefill.
  useEffect(() => {
    if (!editId || !isCustomer()) return;
    getHangoutInvite(editId).then((inv) => {
      if (!inv.isHost) { setLoadError({ status: 403, code: 'forbidden', message: 'Only the host can edit this invite.' }); return; }
      const d = new Date(inv.inviteDateTime);
      setVenueId(inv.venue.id);
      setDate(toDateInput(d));
      setTime(toTimeInput(d));
      setInviteType(inv.inviteType);
      setMessage(inv.message ?? '');
      setOfferId(inv.offer?.id ?? null);
    }).catch((err) => setLoadError(toApiError(err)));
  }, [editId]);

  useEffect(() => {
    if (!venueId || !isCustomer()) return;
    getHangoutVenue(venueId).then(setVenue).catch((err) => setLoadError(toApiError(err)));
    getVenueOffers(venueId).then((list) => {
      setOffers(list);
      // New invites default to the venue's headline (featured-first) offer.
      if (!editId) setOfferId((current) => current ?? list[0]?.id ?? null);
    }).catch(() => setOffers([]));
  }, [venueId, editId]);

  if (!isCustomer()) return <Navigate to={loginUrlFor(location.pathname)} replace />;

  const offer = offers.find((o) => o.id === offerId);
  const offerDto = offer ? { id: offer.id, title: offer.title, benefit: offer.benefit, validity: undefined } : null;

  const handleContinue = async () => {
    const found = validateCreateInvite({ venueId, date, time, inviteType, message });
    setErrors(found);
    if (Object.keys(found).length > 0 || !venueId || !inviteType) return;
    setSaving(true);
    setSaveError(null);
    const input = { venueId, offerId, inviteDateTime: toOffsetIso(date, time), inviteType, message: message.trim() || null };
    try {
      let id = editId ?? readDraft(venueId);
      if (id) {
        try {
          await updateHangoutInvite(id, input);
        } catch (err) {
          // The remembered draft may be gone or already published; start a fresh one then.
          if (editId || !['not_found', 'cancelled', 'expired', 'forbidden'].includes(toApiError(err).code ?? '')) throw err;
          id = null;
        }
      }
      if (!id) {
        const created = await createHangoutInvite(input);
        id = created.id;
        try { sessionStorage.setItem(draftKey(venueId), String(id)); } catch { /* ignore */ }
        trackEvent('hangout_invite_created', { invite_type: inviteType, has_offer: !!offerId, has_message: !!message.trim() });
      }
      navigate(`/invites/${id}/preview`);
    } catch (err) {
      setSaveError(toApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const title = editId ? 'Edit Invite' : 'Create Invite';
  if (loadError) {
    return <main className="hg"><div className="hg-wrap"><PageTop title={title} /><ErrorState title="Can't open this invite" body={loadError.message} /></div></main>;
  }

  return (
    <main className="hg">
      <div className="hg-wrap">
        <PageTop title={title} />
        {venue ? <VenueInviteCard venue={venue} offer={offerDto} /> : <Skeleton height={102} />}

        {offers.length > 0 && (
          <div className="hg-field" style={{ marginTop: 10 }}>
            <label className="hg-section-label" htmlFor="hg-offer" style={{ display: 'block', margin: '0 0 6px' }}>Applicable offer <small>(optional)</small></label>
            <select id="hg-offer" className="hg-input" value={offerId ?? ''} onChange={(e) => setOfferId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">No offer</option>
              {offers.map((o) => <option key={o.id} value={o.id}>{o.title}{o.benefit ? ` · ${o.benefit}` : ''}</option>)}
            </select>
          </div>
        )}

        <div className="hg-section-label">When are you going?</div>
        <div className="hg-row">
          <div className="hg-field">
            <label htmlFor="hg-date" style={{ position: 'absolute', left: -9999 }}>Date</label>
            <input id="hg-date" type="date" className="hg-input" min={toDateInput(new Date())} value={date} onChange={(e) => setDate(e.target.value)} aria-invalid={!!errors.date} />
            {errors.date && <span className="hg-error">{errors.date}</span>}
          </div>
          <div className="hg-field">
            <label htmlFor="hg-time" style={{ position: 'absolute', left: -9999 }}>Time</label>
            <input id="hg-time" type="time" className="hg-input" value={time} onChange={(e) => setTime(e.target.value)} aria-invalid={!!errors.time} />
            {errors.time && <span className="hg-error">{errors.time}</span>}
          </div>
        </div>

        <div className="hg-section-label">Invite type</div>
        <InviteTypeSelector value={inviteType} onChange={setInviteType} />
        {errors.inviteType && <span className="hg-error">{errors.inviteType}</span>}

        <label className="hg-section-label" htmlFor="hg-message" style={{ display: 'block' }}>Add a message <small>(optional)</small></label>
        <textarea id="hg-message" className="hg-input" value={message} placeholder={DEFAULT_MESSAGE_HINT} onChange={(e) => setMessage(e.target.value)} aria-invalid={!!errors.message} />
        <span className={`hg-counter ${message.length > MAX_INVITE_MESSAGE ? 'over' : ''}`}>{message.length}/{MAX_INVITE_MESSAGE}</span>
        {errors.message && <span className="hg-error">{errors.message}</span>}

        {saveError && <div className="hg-notice error" role="alert">{saveError}</div>}
      </div>

      <div className="hg-sticky">
        <div className="hg-wrap">
          <button type="button" className="hg-btn hg-gold block" disabled={saving || !venue} onClick={handleContinue}>{saving ? 'Saving…' : 'Continue'}</button>
        </div>
      </div>
    </main>
  );
};

export default CreateInvitePage;
